if (!customElements.get('hero-slider')) {
  customElements.define(
    'hero-slider',
    class HeroSlider extends HTMLElement {
      constructor() {
        super();

        this.carousel = null;
        this.slider = null;
        this.isHovered = false;
        this.hasFocus = false;
        this.isEditorPaused = false;
        this.autoplayEnabled = false;

        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleResize = FoxTheme.utils.debounce(() => this.updateHeight(), 100);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      }

      connectedCallback() {
        this.originalSlides = Array.from(this.querySelectorAll('.swiper-wrapper > .swiper-slide'));
        this.autoplayEnabled = this.dataset.autoplay === 'true' && !FoxTheme.config.motionReduced;

        if (this.dataset.enableSlider !== 'true' || this.originalSlides.length < 2) return;

        this.initSlider();
        this.bindEvents();
      }

      disconnectedCallback() {
        this.unbindEvents();
        this.slider?.destroy(true, true);
        this.slider = null;
        this.carousel = null;
      }

      initSlider() {
        const controls = {
          previous: this.querySelector('.swiper-button-prev'),
          next: this.querySelector('.swiper-button-next'),
          pagination: this.querySelector('.swiper-pagination'),
        };
        const sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: true,
          autoHeight: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 4,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
          on: {
            init: () => this.updateHeight(0),
            slideChangeTransitionEnd: () => this.updateHeight(0),
          },
        };

        if (controls.previous && controls.next) {
          sliderOptions.navigation = {
            prevEl: controls.previous,
            nextEl: controls.next,
          };
        }

        if (controls.pagination) {
          sliderOptions.pagination = {
            el: controls.pagination,
            clickable: true,
          };
        }

        if (this.autoplayEnabled) {
          sliderOptions.autoplay = {
            delay: Number.parseInt(this.dataset.autoplayDelay, 10) || 5000,
            disableOnInteraction: false,
          };
        }

        this.carousel = new FoxTheme.Carousel(this, sliderOptions, [
          FoxTheme.Swiper.Autoplay,
          FoxTheme.Swiper.EffectFade,
        ]);
        this.carousel.init();
        this.slider = this.carousel.slider;
        this.updateHeight(0);

        this.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', this.handleResize, { once: true });
        });
      }

      bindEvents() {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        this.addEventListener('keydown', this.handleKeyDown);
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        window.addEventListener('resize', this.handleResize);
      }

      unbindEvents() {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        this.removeEventListener('keydown', this.handleKeyDown);
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('resize', this.handleResize);
      }

      handleMouseEnter() {
        this.isHovered = true;
        this.pauseAutoplay();
      }

      handleMouseLeave() {
        this.isHovered = false;
        this.resumeAutoplay();
      }

      handleFocusIn() {
        this.hasFocus = true;
        this.pauseAutoplay();
      }

      handleFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;

        this.hasFocus = false;
        this.resumeAutoplay();
      }

      handleKeyDown(event) {
        if (!this.slider || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

        event.preventDefault();
        event.key === 'ArrowLeft' ? this.slider.slidePrev() : this.slider.slideNext();
      }

      handleBlockSelect(event) {
        if (!this.slider) return;

        const selectedSlide = event.target.closest('.swiper-slide');
        const selectedIndex = Number.parseInt(selectedSlide?.dataset.index, 10);
        if (Number.isNaN(selectedIndex)) return;

        this.isEditorPaused = true;
        this.pauseAutoplay();
        this.slider.slideToLoop(selectedIndex, 0);
      }

      handleBlockDeselect() {
        this.isEditorPaused = false;
        this.resumeAutoplay();
      }

      handleVisibilityChange() {
        document.hidden ? this.pauseAutoplay() : this.resumeAutoplay();
      }

      pauseAutoplay() {
        if (!this.autoplayEnabled || !this.slider?.autoplay?.running) return;
        this.slider.autoplay.stop();
      }

      resumeAutoplay() {
        if (
          !this.autoplayEnabled ||
          !this.slider?.autoplay ||
          this.isHovered ||
          this.hasFocus ||
          this.isEditorPaused ||
          document.hidden
        )
          return;
        if (!this.slider.autoplay.running) this.slider.autoplay.start();
      }

      updateHeight(speed = 300) {
        if (!this.slider) return;
        this.slider.update();
        this.slider.updateAutoHeight(speed);
      }
    }
  );
}
